import { Component, OnInit, DestroyRef, inject, Renderer2, ViewChild, ElementRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { filter, catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavService } from 'app/services/nav.service';
import { UserService } from 'app/services/user.service';
import { environment } from 'environments/environment';
import { AnnouncementService } from 'app/services/Announcement/announcement.service';
import { NotificationService } from 'app/services/notification.service';
import { CreateAnnouncementView } from 'app/models/announcements/CreateAnnouncementView';
import { NavigationService } from 'app/services/NavigationService.service';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import {
	AuthenticationResult,
	InteractionStatus,
	PopupRequest,
	RedirectRequest,
	EventMessage,
	EventType,
	EndSessionRequest,
} from '@azure/msal-browser';
import { SignalRHubService } from 'app/services/signalr-hub.service';
import { ClientSettingsPanelService } from 'app/services/client-settings-panel.service';

@Component({
	selector: 'navbar-cmp',
	templateUrl: 'navbar.component.html',
	standalone: false,
})
export class NavbarComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private clientId: string;
	private userId: string;
	private listTitles: any[];
	location: Location;
	public open: boolean = false;
	environmentName: string = environment.name;
	isProduction: boolean = environment.production;
	announcements: CreateAnnouncementView[];
	unreadAnnouncements: any;
	/** Gates the Client Settings gear (EN-3503): SYS_ADMIN or Aclarian Support. */
	canManageClientSettings: boolean;

	@ViewChild('navbar-cmp', { static: false }) button;

	constructor(
		location: Location,
		private navService: NavService,
		private userService: UserService,
		private notifyService: NotificationService,
		private announcementService: AnnouncementService,
		private router: Router,
		private navigation: NavigationService,
		private authService: MsalService,
		private signalRHubService: SignalRHubService,
		private clientSettingsPanelService: ClientSettingsPanelService
	) {
		this.clientId = this.userService.currentUserValue.user.clientId;
		this.userId = this.userService.currentUserValue.user.id;
		this.canManageClientSettings = this.userService.isAdmin();
		this.location = location;
	}

	/** True when the gear should be shown: an admin with a client currently selected. */
	get showClientSettingsGear(): boolean {
		return this.canManageClientSettings && !!this.clientId && this.clientId !== 'undefined';
	}

	/** Opens the Client Settings slide-out panel for the currently selected client (EN-3503). */
	openClientSettings(): void {
		this.clientSettingsPanelService.open(this.clientId);
	}

	ngOnInit() {
		this.navService.routes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
			this.listTitles = data;
		});

		if (this.clientId && this.clientId != 'undefined') {
			this.initializeSignalR();
		}
	}

	private initializeSignalR() {
		this.signalRHubService.initialize(this.userId, this.clientId);
		this.signalRHubService.on('announcement', () => {
			this.getAnnouncement();
		});
		this.signalRHubService.on('announcementStatusChanged', () => {
			this.getAnnouncement();
		});
		this.signalRHubService
			.whenConnected()
			.pipe(
				catchError((err) => {
					console.error('SignalR connection error:', err);
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => this.getAnnouncement());
	}

	getAnnouncement() {
		this.announcementService.getAnnouncementByUserId(this.clientId, this.userId, 1, 10).subscribe(
			(response) => {
				this.announcements = response.filter((x) => !x.announcementFors[0].isRead);
				localStorage.setItem('unreadAnnouncements', response.filter((x) => !x.announcementFors[0].isRead).length);
				this.getUnreadAnnouncementCount();
			},
			() => {
				this.notifyService.showError('Unable to load announcements.', 'Error');
			}
		);
	}

	getUnreadAnnouncementCount() {
		this.unreadAnnouncements = localStorage.getItem('unreadAnnouncements');
	}

	isMobileMenu() {
		return window.outerWidth >= 991;
	}

	getTitle() {
		let title = this.location.prepareExternalUrl(this.location.path());
		if (title.charAt(0) === '#') {
			title = title.slice(1);
		}
		if (this.listTitles) {
			for (let item = 0; item < this.listTitles.length; item++) {
				let parent = this.listTitles[item];
				if (parent.path === title) {
					return parent.title;
				} else if (parent.children) {
					let children_from_url = title.split('/')[2];
					for (let current = 0; current < parent.children.length; current++) {
						let currentPath = parent.children[current].path;
						let components = currentPath.split('/')[0];
						if (components === children_from_url) {
							return parent.children[current].title;
						}
					}
				}
			}
		}
		return 'Back';
	}

	getPath() {
		return this.location.prepareExternalUrl(this.location.path());
	}

	logout() {
		var isSSOLogin = this.userService.isSSOLogin();
		var msalAccount = this.userService.getMsalAccount();
		this.userService.logout();
		if (isSSOLogin) {
			const logoutRequest: EndSessionRequest = {
				account: msalAccount,
				postLogoutRedirectUri: environment.baseUrl,
			};
			this.authService.logout(logoutRequest);
		}
		this.router.navigateByUrl('/authentication/login').then();
	}

	showBackBtn(): boolean {
		return this.navigation.hasHistory();
	}

	showTitle(): boolean {
		return !(this.showBackBtn() === false && this.getTitle() === 'Back');
	}

	back() {
		this.navigation.back();
	}

	closeDropdown(event: Event) {
		const target = (event.target as HTMLElement).closest('.dropdown');
		if (target) {
			const menu = target.querySelector('.dropdown-menu');
			if (menu && menu.classList.contains('show')) {
				menu.classList.remove('show');
			}
		}
	}
}
